import React, { useEffect, useRef, useState } from 'react';

/**
 * SearchableSelect — dropdown yang bisa dicari dengan mengetik.
 * Dipakai untuk memilih sparepart di form inventori (Stok Keluar, Opname, Penyesuaian).
 *
 * Props:
 *  - options: array of { id, code, name, stock, ... }
 *  - value  : id yang sedang dipilih (string/number) atau ''
 *  - onChange(id) : dipanggil saat item dipilih
 *  - placeholder : teks saat kosong
 */
export default function SearchableSelect({ options = [], value, onChange, placeholder = 'Cari & pilih item...' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  const selected = options.find(o => String(o.id) === String(value));
  const q = query.trim().toLowerCase();

  const filtered = q
    ? options.filter(o =>
        String(o.name || '').toLowerCase().includes(q) ||
        String(o.code || '').toLowerCase().includes(q)
      )
    : options;

  const display = (o) => `${o.code || ''}${o.code && o.name ? ' - ' : ''}${o.name || ''}`;

  // Tutup dropdown saat klik di luar komponen
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset query setiap dropdown dibuka ulang
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const pick = (o) => {
    onChange(o.id);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <input
        type="text"
        value={open ? query : (selected ? display(selected) : '')}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={e => { setOpen(true); setQuery(e.target.value); setHighlight(0); }}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, Math.max(filtered.length - 1, 0))); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlight]) pick(filtered[highlight]); }
          else if (e.key === 'Escape') setOpen(false);
        }}
        style={{
          width: '100%', padding: '12px 16px', borderRadius: 'var(--radius)',
          border: '2px solid var(--gray-200)', fontSize: '0.9rem',
          color: 'var(--gray-700)', background: 'var(--gray-50)', boxSizing: 'border-box',
        }}
      />
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 100,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto',
        }}>
          {filtered.length === 0 && (
            <div style={{ padding: '10px 12px', color: '#94a3b8', fontSize: '0.8rem' }}>
              Tidak ada item cocok dengan "{query}"
            </div>
          )}
          {filtered.slice(0, 80).map((o, i) => (
            <div
              key={o.id}
              onMouseDown={e => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: '0.82rem',
                background: i === highlight ? '#f1f5f9' : '#fff',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <strong>{o.code}</strong> {o.name}
              </span>
              {o.stock !== undefined && (
                <span style={{ color: '#94a3b8', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                  stok: {o.stock ?? 0}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
